import React, { useState, useEffect, useMemo } from 'react';
import UpgradeApprovalModal from './UpgradeApprovalModal';
import CombinedPreviewModal from './CombinedPreviewModal';
import ScheduleSlotPickerModal from './ScheduleSlotPickerModal';
import TreatmentSelector from './TreatmentSelector';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { API_BASE_URL } from '../config';
import { authenticatedFetch, apiGet, apiPost, apiPut, apiPatch, apiDelete, parseApiError } from '../utils/api';
import { findAvailableScheduleSlots } from '../utils/scheduleSlotFinder';
import {
  collectRosteredTherapistNames,
  therapistMatchesRosterSet,
  isTherapistSchedulableOnDate,
} from '../utils/rosterUtils';
import {
  bookingNeedsAfterHoursApproval,
  buildAfterHoursConfirmMessage,
} from '../utils/treatmentTiming';
import {
  formatTherapistsDisplay,
  primaryTherapistName,
  therapistNamesFromList,
  resolveTherapistsForConfirm,
} from '../utils/therapistDisplay';

/** Normalize minutes or HH:MM to value for <input type="time"> */
const toTimeInputValue = (time) => {
  if (time == null || time === '') return '';
  if (typeof time === 'number') {
    const h = Math.floor(time / 60);
    const m = time % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }
  const str = String(time).trim();
  if (str.includes(':')) {
    const [h, m] = str.split(':');
    return `${String(parseInt(h, 10)).padStart(2, '0')}:${String(parseInt(m, 10)).padStart(2, '0')}`;
  }
  const mins = parseInt(str, 10);
  if (!Number.isNaN(mins)) return toTimeInputValue(mins);
  return str;
};

const formatTimeDisplay = (time) => {
  const normalized = toTimeInputValue(time);
  if (!normalized) return '-';
  const [h, m] = normalized.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
};

const normalizeGenderCode = (value) => {
  const g = String(value || '').trim().toUpperCase();
  if (!g) return '';
  if (g.startsWith('M')) return 'M';
  if (g.startsWith('F')) return 'F';
  return g;
};

const genderLabelFromCode = (code) => {
  if (code === 'M') return 'Male';
  if (code === 'F') return 'Female';
  return '';
};

const flattenTherapists = (therapistsConfig) => (
  Array.isArray(therapistsConfig)
    ? therapistsConfig
    : Object.values(therapistsConfig || {}).flat()
);

const therapistHasSkillForTreatment = (therapistName, treatmentName, therapistsConfig) => {
  if (!therapistName || !treatmentName) return false;
  const therapist = flattenTherapists(therapistsConfig).find((t) => t.name === therapistName);
  return (therapist?.skills || []).includes(treatmentName);
};

const therapistsFromSchedulePreview = (preview) => {
  if (!preview) {
    return { therapist: '', therapists: [] };
  }
  if (Array.isArray(preview.therapists) && preview.therapists.length > 0) {
    const primary = preview.therapists.find((t) => t.is_primary) || preview.therapists[0];
    return {
      therapist: preview.therapist || primary?.name || '',
      therapists: preview.therapists,
    };
  }
  if (preview.therapist) {
    return {
      therapist: preview.therapist,
      therapists: [{ name: preview.therapist, is_primary: true }],
    };
  }
  return { therapist: '', therapists: [] };
};

const pickFirstQualifiedTherapist = (treatmentName, therapistsConfig) => {
  const match = flattenTherapists(therapistsConfig).find((therapist) =>
    (therapist?.skills || []).includes(treatmentName)
  );
  return match?.name || '';
};

const resolveGuestGender = (booking, guestContext = null) => {
  const candidates = [
    guestContext?.gender,
    booking?.guestGender,
    booking?.guest_gender,
    booking?.gender,
    booking?.guest?.gender,
  ];
  for (const candidate of candidates) {
    const normalized = normalizeGenderCode(candidate);
    if (normalized) return normalized;
  }

  return '';
};

const EditBookingModal = ({
  booking, 
  config, 
  onClose, 
  onDelete,
  show,
  onSchedulingComplete,
  guestContext = null,
}) => {
  const [editFormData, setEditFormData] = useState({
    date: '',
    treatment: '',
    guestName: '',
    room: '',
    therapist: '',
    therapists: [],
    preferredTime: '',
    preferredTimeOfDay: '',
    genderPreference: '',
    avoidTimes: [],
    generalInstruction: '',
    treatmentInstruction: '',
    status: '',
    duration: null, // Custom duration in minutes
    customDuration: false // Flag to track if duration was manually changed
  });
  
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showAdditionalPreferences, setShowAdditionalPreferences] = useState(false);
  
  // Guest room and in-room therapy state
  const [guestRoom, setGuestRoom] = useState('');
  const [guestRoomTherapyAvailable, setGuestRoomTherapyAvailable] = useState(false);
  const [allRoomsData, setAllRoomsData] = useState({});

  // Upgrade detection state
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradePreview, setUpgradePreview] = useState(null);
  const [upgradeApproval, setUpgradeApproval] = useState(null);
  const [pendingScheduleResult, setPendingScheduleResult] = useState(null);
  const [upgradeApprovedFlag, setUpgradeApprovedFlag] = useState(false);
  
  // Combined preview state (for new flow)
  const [showCombinedModal, setShowCombinedModal] = useState(false);
  const [schedulePreview, setSchedulePreview] = useState(null);
  const [combinedModalConfirmedFlag, setCombinedModalConfirmedFlag] = useState(false);
  const [showCombinedSuccess, setShowCombinedSuccess] = useState(false);
  const [combinedSuccessMessage, setCombinedSuccessMessage] = useState('');
  const [combinedModalError, setCombinedModalError] = useState(null);
  const [treatmentChangeNotice, setTreatmentChangeNotice] = useState('');

  // Store the original booking date for deletion
  const [originalBookingDate, setOriginalBookingDate] = useState('');

  /**
   * Package Scheduling passes scheduleBillingIsPackageInclusion on the booking object.
   * Without is_package_service, repository.saveBooking defaults to chargeable "standalone" billing.
   */
  const applyPackageScheduleBillingToConfirm = (confirmPayload) => {
    if (!booking?.scheduleBillingIsPackageInclusion) return;
    if (confirmPayload.upgrade_data) return;
    if (confirmPayload.is_package_service !== undefined) return;
    confirmPayload.is_package_service = true;
    if (booking.package_name_for_billing) {
      confirmPayload.package_name = booking.package_name_for_billing;
    }
    if (booking.package_id_for_billing) {
      confirmPayload.package_id = booking.package_id_for_billing;
    }
  };

  /** Preserve package entitlement ceiling when editing/downgrading package inclusions */
  const applyPackageEntitlementToConfirm = (confirmPayload, preview) => {
    const entitlementId =
      booking.original_treatment_id ||
      preview?.package_entitlement_treatment_id ||
      preview?.original_treatment_id;
    const entitlementTier =
      booking.original_tier_value ||
      preview?.package_entitlement_tier_value ||
      preview?.original_tier_value;
    const entitlementName =
      booking.original_treatment ||
      preview?.package_entitlement_treatment_name ||
      preview?.original_treatment_name;

    if (entitlementId) {
      confirmPayload.original_treatment_id = entitlementId;
    }
    if (entitlementTier !== undefined && entitlementTier !== null) {
      confirmPayload.original_tier_value = entitlementTier;
    }
    if (entitlementName) {
      confirmPayload.original_treatment = entitlementName;
    }
  };

  const getTherapistsRequired = (treatmentName) => {
    const treatment = config.treatments?.find((t) => t.name === treatmentName);
    return treatment?.therapists_required || 1;
  };

  const syncTherapistsFromPreview = (preview, rawData = {}, previousTherapist = '') => {
    const selection = applyScheduleTherapistSelection(
      preview,
      preview.treatment || editFormData.treatment,
      previousTherapist,
    );
    const therapists = resolveTherapistsForConfirm({
      formTherapists: selection.therapists,
      previewTherapists: preview.therapists || rawData.therapists || [],
      fallbackTherapist: selection.therapist || preview.therapist,
      therapistsRequired: getTherapistsRequired(preview.treatment || editFormData.treatment),
    });
    const therapist = primaryTherapistName(therapists, selection.therapist || preview.therapist);
    return { therapist, therapists };
  };

  const buildConfirmPayload = (normalizedSchedule, rawData = {}, extras = {}) => {
    const treatmentName = normalizedSchedule.treatment || editFormData.treatment;
    const therapists = resolveTherapistsForConfirm({
      formTherapists: editFormData.therapists,
      previewTherapists: normalizedSchedule.therapists || rawData.therapists || [],
      fallbackTherapist: normalizedSchedule.therapist,
      therapistsRequired: getTherapistsRequired(treatmentName),
    });
    const therapist = primaryTherapistName(therapists, normalizedSchedule.therapist);

    return {
      guestName: normalizedSchedule.guestName || editFormData.guestName,
      guestId: booking.guestId || booking.guest_id,
      treatment: treatmentName,
      startTime: normalizedSchedule.startTime,
      endTime: normalizedSchedule.endTime,
      room: normalizedSchedule.room,
      therapist,
      therapists,
      date: normalizedSchedule.date,
      generalInstruction: editFormData.generalInstruction,
      treatmentInstruction: editFormData.treatmentInstruction,
      status: editFormData.status || booking.status,
      currentBookingId: booking?.id || booking?.bookingId,
      duration: editFormData.duration,
      customDuration: editFormData.customDuration,
      ...extras,
    };
  };

  const normalizeSchedulePreview = (preview) => {
    if (!preview) return null;
    const therapists = preview.therapists?.length
      ? preview.therapists
      : preview.therapist
        ? [{ name: preview.therapist, is_primary: true }]
        : [];
    const therapist = primaryTherapistName(therapists, preview.therapist || booking.therapist);
    return {
      ...preview,
      startTime: preview.startTime || preview.start_time || booking.startTime || booking.start_time,
      endTime: preview.endTime || preview.end_time || booking.endTime || booking.end_time,
      treatment: preview.treatment || editFormData.treatment || booking.treatment || booking.service,
      date: preview.date || editFormData.date || booking.date,
      guestName: preview.guestName || preview.guest_name || editFormData.guestName,
      room: preview.room || booking.room,
      therapist,
      therapists,
    };
  };

  const validateSchedulePreview = (scheduleData) => {
    if (!scheduleData || scheduleData.error) {
      throw new Error(scheduleData?.error || 'No available slot found for this treatment');
    }
    const normalized = normalizeSchedulePreview(scheduleData);
    if (!normalized.startTime || !normalized.endTime) {
      throw new Error('No available time slot found for this treatment change');
    }
    if (!normalized.treatment) {
      throw new Error('Could not determine treatment for this booking change');
    }
    return normalized;
  };

  const buildBookingRestorePayload = () => ({
    guestName: booking.guestName || booking.guest_name || editFormData.guestName,
    guestId: booking.guestId || booking.guest_id,
    treatment: booking.treatment || booking.service,
    startTime: booking.startTime || booking.start_time,
    endTime: booking.endTime || booking.end_time,
    room: booking.room,
    therapist: booking.therapist,
    therapists: booking.therapists || [],
    date: originalBookingDate || booking.date,
    generalInstruction: booking.generalInstruction || editFormData.generalInstruction || '',
    treatmentInstruction: booking.treatmentInstruction || editFormData.treatmentInstruction || '',
    status: booking.status || editFormData.status || 'confirmed',
    duration: booking.duration || editFormData.duration,
    customDuration: booking.custom_duration ?? editFormData.customDuration,
  });

  // Therapist replacement modal state
  const [showTherapistReplaceModal, setShowTherapistReplaceModal] = useState(false);
  const [therapistToReplace, setTherapistToReplace] = useState(null); // { name, index, isPrimary }
  const [qualifiedTherapists, setQualifiedTherapists] = useState([]);
  const [loadingQualifiedTherapists, setLoadingQualifiedTherapists] = useState(false);

  // Available slot picker (user opens explicitly)
  const [showSlotPicker, setShowSlotPicker] = useState(false);
  const [slotPickerLoading, setSlotPickerLoading] = useState(false);
  const [slotPickerSlots, setSlotPickerSlots] = useState([]);
  const [slotPickerHints, setSlotPickerHints] = useState([]);

  const formatDateMMDDYYYY = (iso) => {
    if (!iso || typeof iso !== 'string') return iso || '';
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return iso;
    const [, yyyy, mm, dd] = m;
    return `${mm}/${dd}/${yyyy}`;
  };

  // Load rooms data for in-room therapy detection
  useEffect(() => {
    const loadRoomsData = async () => {
      try {
        const response = await apiGet('/api/config');
        if (response.ok) {
          const data = await response.json();
          setAllRoomsData(data.rooms || {});
        }
      } catch (err) {
        console.error('Failed to fetch rooms data:', err);
      }
    };
    loadRoomsData();
  }, []);

  const checkInRoomTherapy = (roomNumber) => {
    if (roomNumber && allRoomsData && Object.keys(allRoomsData).length > 0) {
      const guestRoomKey = `Room No:${roomNumber}`;
      const matchingRoom = Object.entries(allRoomsData).find(
        ([roomName, roomData]) => roomData.location === guestRoomKey
      );
      
      if (matchingRoom) {
        setEditFormData(prev => ({
          ...prev,
          room: matchingRoom[0]
        }));
        setGuestRoomTherapyAvailable(true);
      } else {
        setGuestRoomTherapyAvailable(false);
      }
    } else {
      setGuestRoomTherapyAvailable(false);
    }
  };

  useEffect(() => {
    if (booking) {
      // Clear transient notices from a prior edit session
      setTreatmentChangeNotice('');
      setSchedulePreview(null);
      setError(null);
      setCombinedModalError(null);
      setShowCombinedSuccess(false);
      setCombinedSuccessMessage('');
      setCombinedModalConfirmedFlag(false);
      setPendingScheduleResult(null);

      setOriginalBookingDate(booking.date);
      const bookingGuestRoom = booking.guestRoom || booking.guest_room || '';
      setGuestRoom(bookingGuestRoom);
      
      checkInRoomTherapy(bookingGuestRoom);
      
      // Get duration from booking or treatment default
      const treatment = config.treatments?.find(t => t.name === booking.treatment);
      const defaultDuration = treatment?.duration || 50;
      const bookingDuration = booking.duration || booking.custom_duration || defaultDuration;
      const hasCustomDuration = booking.custom_duration !== null && booking.custom_duration !== undefined;
      const guestGenderCode = resolveGuestGender(booking, guestContext);
      const defaultGenderPreference =
        booking.genderPreference ||
        booking.gender_preference ||
        genderLabelFromCode(guestGenderCode);
      
      setEditFormData({
        date: booking.date,
        treatment: booking.treatment,
        guestName: booking.guestName || booking.guest_name,
        room: booking.room || '',
        therapist: booking.therapist || '',
        therapists: booking.therapists?.length
          ? booking.therapists
          : (booking.therapist ? [{ name: booking.therapist, is_primary: true }] : []),
        preferredTime: toTimeInputValue(
          booking.preferredTime || booking.startTime || booking.start_time || ''
        ),
        preferredTimeOfDay: booking.preferredTimeOfDay || '',
        genderPreference: defaultGenderPreference,
        avoidTimes: [],
        generalInstruction: booking.generalInstruction || '',
        treatmentInstruction: booking.treatmentInstruction || '',
        status: booking.status || 'confirmed',
        duration: bookingDuration,
        customDuration: hasCustomDuration
      });
      
      // Check if this booking has existing upgrade data
      if (booking.is_upgrade && booking.upgrade_amount > 0) {
        console.log('📊 [LOAD] Booking has upgrade data:', {
          is_upgrade: booking.is_upgrade,
          upgrade_amount: booking.upgrade_amount,
          upgrade_status: booking.upgrade_status,
          upgrade_discount_amount: booking.upgrade_discount_amount,
          upgrade_reason: booking.upgrade_reason
        });
        
        // Populate the upgradeApproval state so the badge shows
        setUpgradeApproval({
          upgrade_status: booking.upgrade_status || 'payable',
          upgrade_discount_amount: booking.upgrade_discount_amount || 0,
          upgrade_reason: booking.upgrade_reason || '',
          approved_by: booking.upgraded_by || 'Unknown',
          approved_at: booking.upgraded_at || new Date().toISOString()
        });
        setUpgradeApprovedFlag(true);
        
        // Also set the preview data for display
        setUpgradePreview({
          is_upgrade: true,
          upgrade_amount: booking.upgrade_amount,
          tax_rate: 18, // Will be calculated from treatment
          total_additional_cost: booking.upgrade_amount
        });
      } else {
        // Reset upgrade states if no upgrade
        setUpgradeApproval(null);
        setUpgradeApprovedFlag(false);
        setUpgradePreview(null);
      }
    }
  }, [booking, guestContext]);

  const guestGender = useMemo(
    () => resolveGuestGender(booking, guestContext),
    [booking, guestContext]
  );

  const getTreatmentDetails = (treatmentName) => {
    const treatment = config.treatments.find(t => t.name === treatmentName);
    return treatment || { duration: 0, preparation_time: 0 };
  };

  // Fetch bookings for availability checking
  const [bookingsForAvailability, setBookingsForAvailability] = useState([]);
  const [rosterForDate, setRosterForDate] = useState({});
  const [rosterLoaded, setRosterLoaded] = useState(false);
  
  useEffect(() => {
    const fetchBookings = async () => {
      if (!editFormData.date) {
        setBookingsForAvailability([]);
        return;
      }
      try {
        const response = await apiGet(`/api/bookings/${editFormData.date}`);
        if (response.ok) {
          const bookings = await response.json();
          setBookingsForAvailability(bookings || []);
        } else {
          setBookingsForAvailability([]);
        }
      } catch (error) {
        console.error('Failed to fetch bookings for availability:', error);
        setBookingsForAvailability([]);
      }
    };
    fetchBookings();
  }, [editFormData.date]);

  // Fetch roster assignments for the selected date (to check if therapist is scheduled to work)
  useEffect(() => {
    const fetchRosterForDate = async () => {
      if (!editFormData.date) {
        setRosterForDate({});
        setRosterLoaded(false);
        return;
      }

      setRosterLoaded(false);
      try {
        const response = await apiGet(`/api/roster/assignments?start_date=${editFormData.date}&end_date=${editFormData.date}`);
        if (response.ok) {
          const assignments = await response.json();
          setRosterForDate(assignments || {});
        } else {
          setRosterForDate({});
        }
      } catch (error) {
        console.error('Failed to fetch roster for availability check:', error);
        setRosterForDate({});
      } finally {
        setRosterLoaded(true);
      }
    };

    fetchRosterForDate();
  }, [editFormData.date]);

  const therapistOptions = useMemo(() => {
    if (!booking) {
      return Array.isArray(config.therapists) ? config.therapists : [];
    }

    let list = Array.isArray(config.therapists) ? config.therapists : [];
    
    // Step 1: Filter by skills (if treatment is selected)
    if (editFormData.treatment && config.treatments) {
      const treatment = config.treatments.find(t => t.name === editFormData.treatment);
      if (treatment) {
        list = list.filter(therapist => {
          const skills = therapist.skills || [];
          return skills.includes(editFormData.treatment);
        });
      }
    }
    
    // Step 2: Filter by roster once loaded for the selected date
    if (editFormData.date) {
      if (!rosterLoaded) {
        return [];
      }
      const rosteredNames = collectRosteredTherapistNames(rosterForDate, editFormData.date);
      list = list.filter(
        (t) => isTherapistSchedulableOnDate(t, editFormData.date)
          && therapistMatchesRosterSet(t.name, rosteredNames),
      );
    }
    
    // Step 3: Filter by booking availability (if date and preferredTime are set)
    if (editFormData.date && editFormData.preferredTime && editFormData.treatment && config.treatments) {
      const treatment = config.treatments.find(t => t.name === editFormData.treatment);
      const serviceMinutes = treatment
        ? (Number(treatment.duration) || 0) + (Number(treatment.preparation_time) || 0)
        : 0;
      const duration = serviceMinutes || 60;
      
      const toMinutes = (timeStr) => {
        if (!timeStr) return 0;
        const [h, m] = timeStr.split(':').map(Number);
        return h * 60 + m;
      };
      
      const addMinutes = (timeStr, mins) => {
        const totalMins = toMinutes(timeStr) + mins;
        const h = Math.floor(totalMins / 60);
        const m = totalMins % 60;
        return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      };
      
      const bookingStart = editFormData.preferredTime;
      const bookingEnd = addMinutes(bookingStart, duration);
      const currentBookingId = booking.id || booking.bookingId;
      
      const timesOverlap = (start1, end1, start2, end2) => {
        const s1 = toMinutes(start1);
        const e1 = toMinutes(end1);
        const s2 = toMinutes(start2);
        const e2 = toMinutes(end2);
        return !(e1 <= s2 || s1 >= e2);
      };
      
      list = list.filter(therapist => {
        const conflicts = bookingsForAvailability.filter(b => {
          if ((b.id || b.bookingId) === currentBookingId) return false;
          
          const bookingTherapists = b.therapists || [];
          const therapistNames = bookingTherapists.map(t => t.name);
          const isTherapistInBooking = therapistNames.includes(therapist.name) || b.therapist === therapist.name;
          
          if (!isTherapistInBooking) return false;
          
          const bStart = b.startTime || b.start_time;
          const bEnd = b.endTime || b.end_time;
          return timesOverlap(bookingStart, bookingEnd, bStart, bEnd);
        });
        
        return conflicts.length === 0;
      });
    }
    
    // Include the currently selected therapist only if qualified for this treatment
    const names = new Set(list.map(t => (t.name || '').trim().toLowerCase()));
    const current = (editFormData.therapist || '').trim();
    if (
      current
      && !names.has(current.toLowerCase())
      && therapistHasSkillForTreatment(current, editFormData.treatment, config.therapists)
    ) {
      return [...list, { name: current, id: `current-${current}` }];
    }
    return list;
  }, [config.therapists, config.treatments, editFormData.therapist, editFormData.treatment, editFormData.date, editFormData.preferredTime, bookingsForAvailability, rosterForDate, rosterLoaded]);

  const applyScheduleTherapistSelection = (preview, treatmentName, previousTherapist) => {
    const fromPreview = therapistsFromSchedulePreview(preview);
    if (fromPreview.therapist) {
      return fromPreview;
    }
    if (therapistHasSkillForTreatment(previousTherapist, treatmentName, config.therapists)) {
      return {
        therapist: previousTherapist,
        therapists: [{ name: previousTherapist, is_primary: true }],
      };
    }
    const fallbackTherapist = pickFirstQualifiedTherapist(treatmentName, config.therapists);
    return {
      therapist: fallbackTherapist,
      therapists: fallbackTherapist ? [{ name: fallbackTherapist, is_primary: true }] : [],
    };
  };

  const requestSchedulePreview = async (payload) => {
    const postPreview = async (body) => {
      const response = await apiPost('/api/schedule/preview', body);
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(parseApiError(data, 'No available slot found for this treatment'));
      }
      return data;
    };

    let data = await postPreview(payload);
    if (
      data?.error_type === 'AFTER_HOURS_CONFIRMATION_REQUIRED'
      && !payload.allowAfterOperationalEnd
    ) {
      const confirmed = window.confirm(
        `${data.error}\n\nBook this after-hours slot anyway?`,
      );
      if (!confirmed) {
        const cancelled = new Error(data.error);
        cancelled.cancelledAfterHours = true;
        throw cancelled;
      }
      data = await postPreview({ ...payload, allowAfterOperationalEnd: true });
    }
    return data;
  };

  const buildAvailabilityPayload = (overrides = {}) => {
    const treatmentName = overrides.treatment ?? editFormData.treatment;
    const resolvedGenderPreference =
      overrides.genderPreference ??
      editFormData.genderPreference ??
      genderLabelFromCode(guestGender);

    return {
    guestName: editFormData.guestName,
    guestId: booking?.guestId || booking?.guest_id,
    guestGender: overrides.guestGender ?? guestGender,
    guestRoom: guestRoom,
    treatment: treatmentName,
    date: overrides.date ?? editFormData.date,
    preferredTime: overrides.preferredTime ?? editFormData.preferredTime,
    preferredTimeOfDay: overrides.preferredTimeOfDay ?? editFormData.preferredTimeOfDay,
    therapistPreference: overrides.therapistPreference ?? editFormData.therapist,
    therapistPreferences: overrides.therapistPreferences ?? (
      editFormData.therapists?.length > 0
        ? therapistNamesFromList(editFormData.therapists)
        : editFormData.therapist
          ? [editFormData.therapist]
          : undefined
    ),
    roomPreference: overrides.roomPreference ?? editFormData.room,
    genderPreference: resolvedGenderPreference,
    avoidTimes: editFormData.avoidTimes,
    currentBookingId: booking?.id || booking?.bookingId,
    generalInstruction: editFormData.generalInstruction,
    treatmentInstruction: editFormData.treatmentInstruction,
    duration: overrides.duration ?? editFormData.duration,
    customDuration: overrides.customDuration ?? editFormData.customDuration,
  };
  };

  const previewMatchesSelectedTime = (preview, preferredTime) => {
    if (!preview?.startTime || !preferredTime) return false;
    return toTimeInputValue(preview.startTime) === toTimeInputValue(preferredTime);
  };

  const SCHEDULE_AFFECTING_FIELDS = new Set([
    'preferredTime',
    'date',
    'duration',
    'preferredTimeOfDay',
    'therapist',
    'room',
  ]);

  const handleTreatmentChange = async (treatment) => {
    const newTreatment = treatment?.name || '';
    const newDuration = treatment?.duration || 50;
    const treatmentBeforeChange =
      editFormData.treatment || booking.treatment || booking.service;
    
    // Update treatment and reset duration to new treatment's default
    setEditFormData(prev => ({ 
      ...prev, 
      treatment: newTreatment,
      duration: newDuration,
      customDuration: false // Reset custom flag when treatment changes
    }));

    // Reset all preview states when treatment changes
    setUpgradeApproval(null);
    setUpgradeApprovedFlag(false);
    setUpgradePreview(null);
    setSchedulePreview(null);
    setCombinedModalConfirmedFlag(false);
    setShowCombinedSuccess(false);
    setCombinedSuccessMessage('');
    setError(null);
    setTreatmentChangeNotice('');

    const originalTreatment = treatmentBeforeChange;
    
    console.log('🔍 Treatment changed:', {
      originalTreatment,
      newTreatment,
      bookingId: booking.id || booking.bookingId
    });

    if (originalTreatment !== newTreatment && newTreatment) {
      try {
    setIsLoading(true);
        
        // Check availability for the new treatment (keep user on this form — no confirm modal yet)
        console.log('📅 Checking availability for new treatment...');
        const availabilityPayload = buildAvailabilityPayload({
          treatment: newTreatment,
          duration: newDuration,
          customDuration: false,
          therapistPreference: '',
          therapistPreferences: undefined,
        });

        const scheduleData = await requestSchedulePreview(availabilityPayload);
        const normalizedSchedule = validateSchedulePreview(scheduleData);
        console.log('✅ Availability check passed:', normalizedSchedule);
        setSchedulePreview(normalizedSchedule);
        setCombinedModalError(null);

        const requestedTime = toTimeInputValue(editFormData.preferredTime);
        const previewStart = toTimeInputValue(normalizedSchedule.startTime);
        const timeMatchesRequest = requestedTime && previewStart === requestedTime;
        const therapistSelection = syncTherapistsFromPreview(
          normalizedSchedule,
          scheduleData,
          editFormData.therapist || booking.therapist || '',
        );
        const therapistLabel = formatTherapistsDisplay(
          therapistSelection.therapists,
          therapistSelection.therapist,
        );
        const therapistChanged = therapistSelection.therapists.length > 0
          && formatTherapistsDisplay(
            booking.therapists?.length ? booking.therapists : [{ name: booking.therapist, is_primary: true }],
            booking.therapist,
          ) !== therapistLabel;

        setEditFormData((prev) => ({
          ...prev,
          treatment: newTreatment,
          duration: newDuration,
          customDuration: false,
          therapist: therapistSelection.therapist,
          therapists: therapistSelection.therapists,
          room: normalizedSchedule.room || prev.room,
          preferredTime: previewStart || prev.preferredTime,
        }));
        setSchedulePreview({
          ...normalizedSchedule,
          therapist: therapistSelection.therapist,
          therapists: therapistSelection.therapists,
        });

        if (!timeMatchesRequest && requestedTime && previewStart) {
          setTreatmentChangeNotice(
            `No slot at ${formatTimeDisplay(requestedTime)} for ${newTreatment}. Next open slot is ${formatTimeDisplay(previewStart)}–${formatTimeDisplay(normalizedSchedule.endTime)}${therapistLabel ? ` with ${therapistLabel}` : ''}. Save Changes to use it.`
          );
        }

        // Check for upgrade billing — defer combined modal until Save Changes
        console.log('💰 Checking for upgrade...');
        const upgradePreviewResponse = await apiPost('/api/billing/upgrade-preview', {
            booking_id: booking.id || booking.bookingId,
            new_treatment_name: newTreatment,
            current_treatment_name: treatmentBeforeChange,
          });

        if (upgradePreviewResponse.ok) {
          const upgradeData = await upgradePreviewResponse.json();
          console.log('✅ Upgrade preview response:', upgradeData);
          setUpgradePreview(upgradeData);
          if (!timeMatchesRequest && requestedTime && previewStart) {
            // Notice already explains the moved slot
          } else if (upgradeData.is_upgrade) {
            setTreatmentChangeNotice(
              therapistChanged
                ? `Upgrade treatment selected. Therapists updated to ${therapistLabel}. The charge is shown below. Save Changes to review and approve it.`
                : 'Upgrade treatment selected. The charge is shown below. Save Changes to review and approve it.'
            );
          } else {
            const guestPays = parseFloat(upgradeData.total_additional_cost || 0);
            setTreatmentChangeNotice(
              guestPays > 0
                ? `Treatment updated. Guest pays ₹${guestPays.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}.`
                : 'Treatment updated. No additional charge — guest pays ₹0. Saving removes any earlier upgrade on this appointment.'
            );
          }
        } else {
          const detail = await upgradePreviewResponse.json().catch(() => ({}));
          console.error('❌ Upgrade preview API failed:', upgradePreviewResponse.status);
          setUpgradePreview(null);
          setTreatmentChangeNotice(
            detail.detail || 'Could not calculate the charge for this treatment. Nothing will be saved until the charge can be shown.'
          );
        }

    } catch (error) {
        console.error('❌ Error during treatment change:', error);
        const reason = error.message || 'No available slot found for this treatment';
        setError(
          `Could not change this appointment to ${newTreatment}. It is still ${originalTreatment}. ${reason}`
        );

        // Revert treatment selection on error
        setEditFormData(prev => ({ ...prev, treatment: originalTreatment }));
        setSchedulePreview(null);
        setUpgradePreview(null);
    } finally {
      setIsLoading(false);
    }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({ ...prev, [name]: value }));

    if (SCHEDULE_AFFECTING_FIELDS.has(name)) {
      setSchedulePreview(null);
      setUpgradeApproval(null);
      setUpgradeApprovedFlag(false);
      setPendingScheduleResult(null);
      if (name === 'preferredTime' || name === 'date') {
        setTreatmentChangeNotice(
          'Time or date changed — browse available slots or use Check Availability for a custom time, then Save Changes.'
        );
      }
    }
  };

  const clearSchedulePreviewForFieldChange = () => {
    setSchedulePreview(null);
    setUpgradeApproval(null);
    setUpgradeApprovedFlag(false);
    setPendingScheduleResult(null);
    setTreatmentChangeNotice(
      'Schedule details changed — browse available slots or use Check Availability, then Save Changes.'
    );
  };

  const handleOpenSlotPicker = async () => {
    if (!editFormData.date || !editFormData.treatment) {
      setError('Select a date and treatment before browsing slots.');
      return;
    }

    setError(null);
    setShowSlotPicker(true);
    setSlotPickerLoading(true);
    setSlotPickerSlots([]);
    setSlotPickerHints([]);

    try {
      const { slots, failureHints } = await findAvailableScheduleSlots({
        apiPost,
        treatment: editFormData.treatment,
        date: editFormData.date,
        guestName: editFormData.guestName,
        guestId: booking?.guestId || booking?.guest_id,
        guestGender,
        guestRoom,
        genderPreference: editFormData.genderPreference || genderLabelFromCode(guestGender),
        roomPreference: editFormData.room || null,
        therapistPreference: editFormData.therapist || null,
        currentBookingId: booking?.id || booking?.bookingId,
        duration: editFormData.duration,
        customDuration: editFormData.customDuration,
        availableTreatments: config.treatments || [],
        allRoomsData,
      });
      setSlotPickerSlots(slots);
      setSlotPickerHints(failureHints);
    } catch (err) {
      console.error('Slot picker search failed:', err);
      setError(err.message || 'Failed to load available slots');
      setShowSlotPicker(false);
    } finally {
      setSlotPickerLoading(false);
    }
  };

  const handleSlotPickerSelect = (slot) => {
    try {
      const normalizedSchedule = validateSchedulePreview(slot);
      const therapistSelection = syncTherapistsFromPreview(
        normalizedSchedule,
        slot,
        editFormData.therapist || booking.therapist || '',
      );

      setSchedulePreview({
        ...normalizedSchedule,
        therapist: therapistSelection.therapist,
        therapists: therapistSelection.therapists,
      });
      setCombinedModalConfirmedFlag(false);
      setCombinedModalError(null);
      setEditFormData((prev) => ({
        ...prev,
        preferredTime: toTimeInputValue(normalizedSchedule.startTime),
        therapist: therapistSelection.therapist,
        therapists: therapistSelection.therapists,
        room: normalizedSchedule.room || prev.room,
      }));
      setTreatmentChangeNotice(
        `Slot selected: ${formatTimeDisplay(normalizedSchedule.startTime)} – ${formatTimeDisplay(normalizedSchedule.endTime)} with ${formatTherapistsDisplay(therapistSelection.therapists, therapistSelection.therapist) || 'assigned therapists'}. Click Save Changes to apply.`,
      );
      setShowSlotPicker(false);
    } catch (err) {
      setError(err.message || 'Could not apply selected slot');
    }
  };

  const handleConfirmChanges = async () => {
    try {
      setIsLoading(true);
      setError(null);

      console.log('💾 Confirming booking changes...');
      
      const originalTreatment = booking.treatment || booking.service;
      const newTreatment = editFormData.treatment;
      
      if (originalTreatment !== newTreatment && upgradePreview?.is_upgrade && !upgradeApproval) {
        setError('Please approve or cancel the upgrade before applying changes');
        setIsLoading(false);
        return;
      }

      await proceedWithBookingUpdate();

    } catch (error) {
      console.error('Error updating booking:', error);
      setError(error.message);
      onSchedulingComplete({ error: error.message });
    } finally {
      setIsLoading(false);
    }
  };

  const proceedWithBookingUpdate = async () => {
    try {
      const scheduleData = {
        ...buildAvailabilityPayload(),
        guestName: editFormData.guestName,
        guestRoom: guestRoom,
      };

      if (upgradeApproval) {
        scheduleData.upgrade_data = upgradeApproval;
      }

      const scheduleResponse = await apiPost('/api/schedule/preview', scheduleData);

      const scheduleResult = await scheduleResponse.json();
      
      if (!scheduleResponse.ok || scheduleResult.error) {
        throw new Error(parseApiError(scheduleResult, 'Failed to find available slot'));
      }

      const normalizedSchedule = validateSchedulePreview(scheduleResult);

      const confirmPayload = buildConfirmPayload(normalizedSchedule, scheduleResult);

      if (upgradeApproval) {
        confirmPayload.upgrade_data = upgradeApproval;
      }

      applyPackageEntitlementToConfirm(confirmPayload, upgradePreview);
      applyPackageScheduleBillingToConfirm(confirmPayload);

      const confirmResponse = await apiPost('/api/schedule/confirm', confirmPayload);

      const confirmResult = await confirmResponse.json();
      
      if (!confirmResponse.ok) {
        throw new Error(parseApiError(confirmResult, 'Failed to confirm booking'));
      }
      if (confirmResult.error) {
        throw new Error(confirmResult.error);
      }

      if ((booking.id || booking.bookingId) && originalBookingDate && (originalBookingDate !== editFormData.date)) {
        const deleteResponse = await apiDelete(`/api/bookings/${originalBookingDate}/${booking.id || booking.bookingId}`);

        if (!deleteResponse.ok) {
          throw new Error('Failed to delete old booking');
        }
      }

      onSchedulingComplete({ success: true });
      
      // Reset all upgrade states after successful booking
      setUpgradePreview(null);
      setUpgradeApproval(null);
      setUpgradeApprovedFlag(false);
      setPendingScheduleResult(null);
    } catch (error) {
      throw error;
    }
  };

  const handleUpgradeApprove = async (approvalData) => {
    console.log('🟢 [APPROVE] handleUpgradeApprove called with:', approvalData);
    
    // Add approver information (placeholder until user authentication is implemented)
    // TODO: Replace with actual logged-in user when authentication is implemented
    const enrichedApprovalData = {
      ...approvalData,
      approved_by: 'Current User', // Placeholder - will be replaced with actual user
      approved_at: new Date().toISOString()
    };
    
    console.log('🟢 [APPROVE] Setting upgradeApproval state');
    setUpgradeApproval(enrichedApprovalData);
    
    console.log('🟢 [APPROVE] Setting upgradeApprovedFlag = true');
    setUpgradeApprovedFlag(true);
    
    console.log('🟢 [APPROVE] Saving pendingScheduleResult with treatment:', editFormData.treatment);
    setPendingScheduleResult({
      guestName: editFormData.guestName,
      guestRoom: guestRoom,
      guestGender,
      treatment: editFormData.treatment,
      date: editFormData.date,
      preferredTime: editFormData.preferredTime,
      preferredTimeOfDay: editFormData.preferredTimeOfDay,
      therapistPreference: editFormData.therapist,
      roomPreference: editFormData.room,
      genderPreference: editFormData.genderPreference,
      avoidTimes: editFormData.avoidTimes,
      currentBookingId: booking.id,
      generalInstruction: editFormData.generalInstruction,
      treatmentInstruction: editFormData.treatmentInstruction,
      duration: editFormData.duration, // CRITICAL: Custom duration
      customDuration: editFormData.customDuration
    });
    
    console.log('🟢 [APPROVE] Closing modal');
    setShowUpgradeModal(false);
    console.log('🟢 [APPROVE] handleUpgradeApprove complete');
  };

  const handleUpgradeCancel = () => {
    console.log('🔴 [CANCEL] handleUpgradeCancel called');
    console.log('🔴 [CANCEL] Current upgradeApprovedFlag:', upgradeApprovedFlag);
    console.log('🔴 [CANCEL] Current upgradeApproval:', upgradeApproval);
    console.log('🔴 [CANCEL] Current treatment in form:', editFormData.treatment);
    console.log('🔴 [CANCEL] Original booking treatment:', booking.treatment || booking.service);
    
    // Check flag to see if approval was done
    if (upgradeApprovedFlag) {
      console.log('✅ [CANCEL] Flag is TRUE - Upgrade was approved, keeping treatment');
      setUpgradeApprovedFlag(false); // Reset flag
      setShowUpgradeModal(false);
      return;
    }
    
    console.log('❌ [CANCEL] Flag is FALSE - Upgrade was NOT approved, reverting treatment');
    const originalTreatment = booking.treatment || booking.service;
    setEditFormData(prev => ({ ...prev, treatment: originalTreatment }));
    setUpgradePreview(null);
    setUpgradeApproval(null);
    setShowUpgradeModal(false);
    console.log('❌ [CANCEL] Treatment reverted to:', originalTreatment);
  };

  const handleCombinedModalConfirm = async (scheduleData, approvalData) => {
    console.log('✅ [COMBINED] Combined modal confirmed');
    console.log('✅ [COMBINED] Schedule data:', scheduleData);
    console.log('✅ [COMBINED] Approval data:', approvalData);

    let oldBookingDeleted = false;

    try {
      setIsLoading(true);
      setCombinedModalError(null);

      const normalizedSchedule = validateSchedulePreview(schedulePreview || scheduleData);
      if (!normalizedSchedule) {
        throw new Error('No schedule preview available — click Save Changes again');
      }

      const confirmPayload = buildConfirmPayload(normalizedSchedule, schedulePreview || scheduleData);

      // CRITICAL: Determine if this is an upgrade, downgrade, or within-package change
      if (approvalData && upgradePreview) {
        console.log('💰 [COMBINED] Processing treatment change with upgrade preview');
        
        const packageEntitlementTier =
          upgradePreview.package_entitlement_tier_value ?? upgradePreview.original_tier_value;
        const packageEntitlementId =
          upgradePreview.package_entitlement_treatment_id ?? upgradePreview.original_treatment_id;

        if (!packageEntitlementId) {
          console.error('❌ [COMBINED] Backend did not return package entitlement info!');
          throw new Error('Cannot process treatment change: Missing original treatment information');
        }
        
        // Persist package inclusion ceiling (not the current booked service used for display)
        approvalData.original_treatment_id = packageEntitlementId;
        approvalData.original_tier_value = packageEntitlementTier;
        console.log(`💰 [COMBINED] Package entitlement: ${upgradePreview.package_entitlement_treatment_name || upgradePreview.original_treatment_name} (ID=${packageEntitlementId}, Tier Value=₹${packageEntitlementTier})`);
        console.log(`💰 [COMBINED] Upgrade from: ${upgradePreview.original_treatment_name} → ${upgradePreview.new_treatment_name}`);
        
        // Determine if new treatment is within original package tier
        const isWithinPackageTier = upgradePreview.new_tier_value <= packageEntitlementTier;
        const isUpgrade = upgradePreview.is_upgrade;
        const isDowngrade = upgradePreview.is_downgrade;
        
        console.log(`💰 [COMBINED] Analysis: isUpgrade=${isUpgrade}, isDowngrade=${isDowngrade}, isWithinPackage=${isWithinPackageTier}`);
        
        if (isWithinPackageTier && !isUpgrade) {
          // New treatment is within original package tier - no charge
          console.log('✅ [COMBINED] Treatment is within original package tier - no additional charge');
          confirmPayload.is_package_service = true;
          // Don't add upgrade_data - this is a package service
        } else if (isUpgrade) {
          // Treatment is an upgrade - requires payment
          console.log(`💸 [COMBINED] Treatment is an upgrade - requires payment of ₹${upgradePreview.upgrade_amount}`);
          confirmPayload.is_package_service = false;
          confirmPayload.upgrade_data = approvalData;
        } else if (isDowngrade) {
          // Treatment is a downgrade - preserve entitlement, no refund
          console.log('📉 [COMBINED] Treatment is a downgrade - preserving package entitlement');
          confirmPayload.is_package_service = true;
        }
        
        // Store approval for display
        setUpgradeApproval(approvalData);
        setUpgradeApprovedFlag(true);
      } else if (upgradePreview) {
        // No upgrade approval modal (downgrade / within-package) — still apply preview billing logic
        const isWithinPackageTier = upgradePreview.new_tier_value <= upgradePreview.original_tier_value;
        const isUpgrade = upgradePreview.is_upgrade;

        if (isWithinPackageTier && !isUpgrade) {
          confirmPayload.is_package_service = true;
        } else if (isUpgrade) {
          confirmPayload.is_package_service = false;
        } else if (upgradePreview.is_downgrade) {
          confirmPayload.is_package_service = true;
        }
      } else if (approvalData) {
        // Fallback: No upgrade preview available (shouldn't happen)
        console.warn('⚠️ [COMBINED] Approval data exists but no upgrade preview - using legacy fallback');
        confirmPayload.upgrade_data = approvalData;
        setUpgradeApproval(approvalData);
        setUpgradeApprovedFlag(true);
      }

      applyPackageEntitlementToConfirm(confirmPayload, upgradePreview);
      applyPackageScheduleBillingToConfirm(confirmPayload);

      // Delete old booking FIRST (before creating new one) to free up the slot
      if (booking.id || booking.bookingId) {
        console.log('🗑️ [COMBINED] Deleting old booking FIRST:', booking.id || booking.bookingId);
        const bookingDateForDelete = originalBookingDate || booking.date;
        const deleteResponse = await apiDelete(`/api/bookings/${bookingDateForDelete}/${booking.id || booking.bookingId}`);

        if (!deleteResponse.ok) {
          console.error('⚠️ [COMBINED] Failed to delete old booking');
          throw new Error('Failed to delete old booking');
        } else {
          console.log('✅ [COMBINED] Old booking deleted successfully');
          oldBookingDeleted = true;
        }
      }

      // Now create the new booking
      console.log('📤 [COMBINED] Creating new booking:', confirmPayload);

      const confirmResponse = await apiPost('/api/schedule/confirm', confirmPayload);

      const confirmResult = await confirmResponse.json();
      
      if (!confirmResponse.ok) {
        throw new Error(parseApiError(confirmResult, 'Failed to confirm booking'));
      }
      if (confirmResult.error) {
        throw new Error(confirmResult.error);
      }

      console.log('✅ [COMBINED] New booking created successfully:', confirmResult);

      setSchedulePreview({
        ...normalizedSchedule,
        therapist: confirmPayload.therapist,
        therapists: confirmPayload.therapists,
      });

      // Show success message
      console.log('✅ [COMBINED] Showing success confirmation');
      setShowCombinedSuccess(true);
      setCombinedModalError(null);
      
      const upgradeMsg = approvalData 
        ? ` with ${approvalData.upgrade_status === 'complimentary' ? 'complimentary' : approvalData.upgrade_status === 'discounted' ? 'discounted' : 'payable'} upgrade`
        : '';
      
      setCombinedSuccessMessage(`Appointment updated successfully${upgradeMsg}`);

    } catch (error) {
      console.error('❌ [COMBINED] Error applying changes:', error);

      if (oldBookingDeleted) {
        console.warn('⚠️ [COMBINED] Confirm failed after delete — attempting to restore original booking');
        try {
          const restorePayload = buildBookingRestorePayload();
          applyPackageScheduleBillingToConfirm(restorePayload);
          const restoreResponse = await apiPost('/api/schedule/confirm', restorePayload);
          const restoreResult = await restoreResponse.json();
          if (restoreResponse.ok && !restoreResult.error) {
            setCombinedModalError(
              `${error.message} The original appointment was restored.`
            );
          } else {
            setCombinedModalError(
              `${error.message} The original appointment could not be restored automatically — please re-book manually.`
            );
          }
        } catch (restoreError) {
          console.error('❌ [COMBINED] Failed to restore original booking:', restoreError);
          setCombinedModalError(
            `${error.message} The original appointment could not be restored automatically — please re-book manually.`
          );
        }
      } else {
        setCombinedModalError(error.message);
      }

      setError(error.message);
      setCombinedModalConfirmedFlag(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCombinedModalCancel = () => {
    console.log('❌ [COMBINED] Combined modal cancel handler called');
    console.log('❌ [COMBINED] showCombinedSuccess:', showCombinedSuccess);
    console.log('❌ [COMBINED] combinedModalConfirmedFlag:', combinedModalConfirmedFlag);
    
    // If showing success screen, clicking OK should close and refresh
    if (showCombinedSuccess) {
      console.log('✅ [COMBINED] Success OK clicked, closing and refreshing');
      setShowCombinedModal(false);
      setShowCombinedSuccess(false);
      setCombinedSuccessMessage('');
      setCombinedModalError(null);
      
      // Reset all states
      setUpgradePreview(null);
      setUpgradeApproval(null);
      setUpgradeApprovedFlag(false);
      setPendingScheduleResult(null);
      setSchedulePreview(null);
      setCombinedModalConfirmedFlag(false);
      
      // Notify parent to refresh
      onSchedulingComplete({ success: true });
      return;
    }
    
    // Check flag to see if confirmation was done
    if (combinedModalConfirmedFlag) {
      console.log('✅ [COMBINED] Already confirmed, NOT reverting treatment');
      setCombinedModalConfirmedFlag(false); // Reset flag
      setShowCombinedModal(false);
      return;
    }
    
    console.log('❌ [COMBINED] User cancelled combined modal — keeping edits in form');
    setCombinedModalError(null);
    setShowCombinedModal(false);
  };

  const handleSaveChanges = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const id = booking.id || booking.bookingId;
      if (!id) throw new Error('Missing booking id');
      
      // Check what changed
      const originalTreatment = booking.treatment || booking.service;
      const originalDate = booking.date;
      const originalTime = toTimeInputValue(booking.startTime || booking.start_time);
      const currentTime = toTimeInputValue(editFormData.preferredTime);
      const originalTherapist = booking.therapist;
      const originalDuration = booking.duration || booking.custom_duration;
      
      const treatmentChanged = originalTreatment !== editFormData.treatment;
      const dateChanged = originalDate !== editFormData.date;
      const timeChanged = originalTime !== currentTime;
      const therapistChanged = originalTherapist !== editFormData.therapist;
      const durationChanged = editFormData.customDuration && originalDuration !== editFormData.duration;
      
      console.log('💾 [SAVE] Changes detected:', {
        treatmentChanged,
        dateChanged,
        timeChanged,
        therapistChanged,
        durationChanged,
        originalDuration,
        newDuration: editFormData.duration
      });
      
      // If major changes (date/time/treatment/therapist/duration), need to check availability & rebook
      if (dateChanged || timeChanged || treatmentChanged || therapistChanged || durationChanged) {
        console.log('🔄 [SAVE] Major changes detected, rebooking appointment');
        
        // Rebook only when upgrade was approved and schedule fields did not change since
        if (upgradeApproval && !timeChanged && !dateChanged && !treatmentChanged && !therapistChanged && !durationChanged) {
          console.log('✅ [SAVE] Upgrade already approved with unchanged schedule, proceeding with booking update');
          await proceedWithBookingUpdate();
          return;
        }

        if (upgradeApproval && (timeChanged || dateChanged || treatmentChanged || therapistChanged || durationChanged)) {
          console.log('🔄 [SAVE] Schedule changed after upgrade approval — re-checking availability');
          setUpgradeApproval(null);
          setUpgradeApprovedFlag(false);
          setPendingScheduleResult(null);
        }
        
        // Check availability for the changes
        const availabilityPayload = buildAvailabilityPayload();
        const scheduleData = await requestSchedulePreview(availabilityPayload);
        const normalizedSchedule = validateSchedulePreview(scheduleData);
        const therapistSelection = syncTherapistsFromPreview(
          normalizedSchedule,
          scheduleData,
          editFormData.therapist || booking.therapist || '',
        );
        setEditFormData((prev) => ({
          ...prev,
          therapist: therapistSelection.therapist,
          therapists: therapistSelection.therapists,
          room: normalizedSchedule.room || prev.room,
        }));
        const previewForModal = {
          ...normalizedSchedule,
          therapist: therapistSelection.therapist,
          therapists: therapistSelection.therapists,
        };
        setSchedulePreview(previewForModal);
        console.log('✅ [SAVE] Availability confirmed:', previewForModal);

        // Upgrade treatment: show billing confirmation only after user has set time/date
        let activeUpgradePreview = upgradePreview;
        if (treatmentChanged && !activeUpgradePreview) {
          const upgradePreviewResponse = await apiPost('/api/billing/upgrade-preview', {
            booking_id: booking.id || booking.bookingId,
            new_treatment_name: editFormData.treatment,
            current_treatment_name: originalTreatment,
          });
          if (upgradePreviewResponse.ok) {
            activeUpgradePreview = await upgradePreviewResponse.json();
            setUpgradePreview(activeUpgradePreview);
          }
        }

        if (treatmentChanged && !activeUpgradePreview) {
          setError('Could not calculate the charge for this treatment change. Nothing was saved.');
          return;
        }

        if (treatmentChanged && activeUpgradePreview?.is_upgrade && !upgradeApproval) {
          setShowCombinedModal(true);
          setTreatmentChangeNotice('');
          return;
        }
        
        // Delete old booking first
        const bookingDateForDelete = originalBookingDate || booking.date;
        console.log('🗑️ [SAVE] Deleting old booking:', booking.id || booking.bookingId);
        const deleteResponse = await apiDelete(`/api/bookings/${bookingDateForDelete}/${booking.id || booking.bookingId}`);

        if (!deleteResponse.ok) {
          console.error('⚠️ [SAVE] Failed to delete old booking');
          throw new Error('Failed to delete old booking');
        }
        console.log('✅ [SAVE] Old booking deleted');

        const confirmPayload = buildConfirmPayload(normalizedSchedule, scheduleData);

        // Add upgrade data if present
        if (upgradeApproval) {
          confirmPayload.upgrade_data = upgradeApproval;
        }

        applyPackageEntitlementToConfirm(confirmPayload, activeUpgradePreview);
        if (
          treatmentChanged &&
          activeUpgradePreview &&
          !activeUpgradePreview.is_upgrade &&
          activeUpgradePreview.source_is_package_service
        ) {
          confirmPayload.is_package_service = true;
          confirmPayload.upgrade_amount = 0;
        }
        applyPackageScheduleBillingToConfirm(confirmPayload);

        console.log('📤 [SAVE] Creating new booking:', confirmPayload);
        const confirmResponse = await apiPost('/api/schedule/confirm', confirmPayload);

        const confirmResult = await confirmResponse.json();
        
        if (!confirmResponse.ok) {
          throw new Error(parseApiError(confirmResult, 'Failed to confirm booking'));
        }
        if (confirmResult.error) {
          throw new Error(confirmResult.error);
        }

        console.log('✅ [SAVE] Booking updated successfully!');
        onSchedulingComplete({ success: true });
        
      } else {
        // Only instructions/status changed - quick save
        console.log('📝 [SAVE] Only instructions/status changed, doing quick update');
        
      const payload = {
        date: editFormData.date,
        generalInstruction: editFormData.generalInstruction,
        treatmentInstruction: editFormData.treatmentInstruction,
      };
        
      const resp = await apiPatch(`/api/bookings/${id}/instructions`, payload);
        
      const j = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(j.detail || 'Failed to save instructions');
        
        // Update status if changed (normalize defaults so missing status ≠ spurious update)
        const originalStatus = String(booking.status || 'confirmed').toLowerCase();
        const newStatus = String(editFormData.status || 'confirmed').toLowerCase();
        if (newStatus !== originalStatus) {
          const statusResp = await apiPut(
            `/api/bookings/${id}/status?status=${encodeURIComponent(editFormData.status)}`,
            {}
          );
          if (!statusResp.ok) {
            const j = await statusResp.json().catch(() => ({}));
            throw new Error(j.detail || 'Failed to update status');
          }
        }
        
        console.log('✅ [SAVE] Instructions/status saved');
      onSchedulingComplete({ success: true });
      }
      
    } catch (err) {
      console.error('❌ [SAVE] Error:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckAvailability = async () => {
    setError(null);
    setIsLoading(true);
    try {
      const treatment = config.treatments?.find((t) => t.name === editFormData.treatment);
      let allowAfterOperationalEnd = false;
      if (bookingNeedsAfterHoursApproval(treatment, editFormData.preferredTime)) {
        const confirmed = window.confirm(
          buildAfterHoursConfirmMessage(treatment, editFormData.preferredTime),
        );
        if (!confirmed) {
          setIsLoading(false);
          return;
        }
        allowAfterOperationalEnd = true;
      }

      const availabilityPayload = {
        ...buildAvailabilityPayload(),
        allowAfterOperationalEnd,
      };
      const responseData = await requestSchedulePreview(availabilityPayload);

      const normalizedSchedule = validateSchedulePreview(responseData);
      setCombinedModalError(null);
      const therapistSelection = syncTherapistsFromPreview(
        normalizedSchedule,
        responseData,
        editFormData.therapist,
      );
      setEditFormData((prev) => ({
        ...prev,
        therapist: therapistSelection.therapist,
        therapists: therapistSelection.therapists,
        room: normalizedSchedule.room || prev.room,
        preferredTime:
          toTimeInputValue(normalizedSchedule.startTime) || prev.preferredTime,
      }));
      setSchedulePreview({
        ...normalizedSchedule,
        therapist: therapistSelection.therapist,
        therapists: therapistSelection.therapists,
      });
      setTreatmentChangeNotice(
        previewMatchesSelectedTime(normalizedSchedule, editFormData.preferredTime)
          ? 'Slot confirmed for your selected time. Click Save Changes to apply.'
          : `Available slot found at ${formatTimeDisplay(normalizedSchedule.startTime)}. Adjust time if needed, then Save Changes.`
      );
    } catch (error) {
      setError(error.message);
      setSchedulePreview(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle opening therapist replacement modal
  const handleEditTherapist = async (therapist, index) => {
    setTherapistToReplace({ 
      name: therapist.name, 
      index, 
      isPrimary: therapist.is_primary 
    });
    setShowTherapistReplaceModal(true);
    
    // Fetch qualified therapists for this treatment
    setLoadingQualifiedTherapists(true);
    try {
      console.log('🔍 DEBUG: Fetching qualified therapists for replacement');
      console.log('   Current booking therapists:', booking.therapists);
      console.log('   Therapist being replaced:', therapist);
      console.log('   Booking time slot:', booking.date, booking.startTime || booking.start_time, '-', booking.endTime || booking.end_time);
      
      const treatment = config.treatments.find(t => t.name === editFormData.treatment);
      if (!treatment) {
        setError('Treatment not found');
        setLoadingQualifiedTherapists(false);
        return;
      }
      
      console.log('   Treatment:', editFormData.treatment, '- Gender specific:', treatment.gender_specific);

      // Get all bookings for this date to check availability
      let existingBookings = [];
      try {
        const bookingsResponse = await apiGet(`/api/bookings/${booking.date}`);
        if (bookingsResponse.ok) {
          existingBookings = await bookingsResponse.json();
          console.log(`   Fetched ${existingBookings.length} existing bookings for ${booking.date}`);
        }
      } catch (err) {
        console.error('Failed to fetch bookings for availability check:', err);
      }

      // Get roster assignments for this date to check if therapist is scheduled to work
      let rosteredTherapistNames = new Set();
      let rosterFetchOk = false;
      try {
        const rosterResponse = await apiGet(`/api/roster/assignments?start_date=${booking.date}&end_date=${booking.date}`);
        if (rosterResponse.ok) {
          rosterFetchOk = true;
          const rosterData = await rosterResponse.json();
          rosteredTherapistNames = collectRosteredTherapistNames(rosterData, booking.date);
          console.log(`   Found ${rosteredTherapistNames.size} therapists rostered for ${booking.date}`);
        }
      } catch (err) {
        console.error('Failed to fetch roster for availability check:', err);
      }

      // Helper function to check time overlap
      const timesOverlap = (start1, end1, start2, end2) => {
        // Convert time strings to minutes for comparison
        const toMinutes = (timeStr) => {
          if (!timeStr) return 0;
          const [h, m] = timeStr.split(':').map(Number);
          return h * 60 + m;
        };
        const s1 = toMinutes(start1);
        const e1 = toMinutes(end1);
        const s2 = toMinutes(start2);
        const e2 = toMinutes(end2);
        return !(e1 <= s2 || s1 >= e2); // Overlap if NOT (end1 before start2 OR start1 after end2)
      };

      const bookingStartTime = booking.startTime || booking.start_time;
      const bookingEndTime = booking.endTime || booking.end_time;
      const currentBookingId = booking.id || booking.bookingId;

      // Get all therapists who:
      // 1. Have the required skills (can perform this treatment)
      // 2. Match gender requirements (if treatment is gender-specific)
      // 3. Are not already assigned to this booking
      // 4. Are available at this time slot (no conflicting bookings)
      const allTherapists = Array.isArray(config.therapists) 
        ? Object.values(config.therapists).flat()
        : Object.values(config.therapists || {});
      
      const currentTherapistNames = (booking.therapists || []).map(t => t.name);
      const treatmentGenderSpecific = treatment.gender_specific?.toUpperCase() === 'YES';
      const therapistsRequired = treatment.therapists_required || 1;
      
      let requiredGender = null;
      const guestGenderCode = resolveGuestGender(booking, guestContext);
      if (treatmentGenderSpecific && guestGenderCode) {
        requiredGender = guestGenderCode === 'M' ? 'MALE' : guestGenderCode === 'F' ? 'FEMALE' : guestGenderCode;
      } else if ((treatmentGenderSpecific || therapistsRequired > 1) && editFormData.genderPreference) {
        const pref = editFormData.genderPreference.trim().toUpperCase();
        requiredGender = pref.startsWith('M') ? 'MALE' : pref.startsWith('F') ? 'FEMALE' : pref;
      }
      
      if (requiredGender) {
        console.log(`🚻 Treatment requires gender match: ${requiredGender}`);
      }

      const qualified = allTherapists.filter(t => {
        // 1. Check if therapist has the skill to perform this treatment
        const therapistSkills = t.skills || [];
        if (!therapistSkills.includes(editFormData.treatment)) {
          console.log(`❌ Filtered out ${t.name}: Missing skill for ${editFormData.treatment}`);
          return false;
        }
        
        if (!isTherapistSchedulableOnDate(t, booking.date)) {
          console.log(`❌ Filtered out ${t.name}: Not active on ${booking.date}`);
          return false;
        }

        // 2. Check if therapist is rostered for this date
        if (rosterFetchOk && !therapistMatchesRosterSet(t.name, rosteredTherapistNames)) {
          console.log(`❌ Filtered out ${t.name}: Not rostered for ${booking.date}`);
          return false;
        }
        
        // 3. Skip if already assigned (except the one being replaced)
        if (currentTherapistNames.includes(t.name) && t.name !== therapist.name) {
          console.log(`❌ Filtered out ${t.name}: Already assigned`);
          return false;
        }
        
        // 4. Check gender match if required
        if (requiredGender) {
          const tGender = (t.gender || '').trim().toUpperCase();
          const normalizedGender = tGender.startsWith('M') ? 'MALE' : tGender.startsWith('F') ? 'FEMALE' : tGender;
          
          console.log(`🔍 Checking ${t.name}: Gender="${t.gender}" → Normalized="${normalizedGender}" vs Required="${requiredGender}"`);
          
          if (normalizedGender !== requiredGender) {
            console.log(`❌ Filtered out ${t.name}: Gender mismatch (need ${requiredGender}, has ${normalizedGender})`);
            return false;
          }
        }
        
        // 5. Check availability - therapist should NOT have conflicting bookings
        const conflictingBookings = existingBookings.filter(b => {
          // Skip the current booking being edited
          if ((b.id || b.bookingId) === currentBookingId) {
            return false;
          }
          
          // Check if this booking involves the therapist
          const bookingTherapists = b.therapists || [];
          const therapistNames = bookingTherapists.map(th => th.name);
          const isTherapistInBooking = therapistNames.includes(t.name) || b.therapist === t.name;
          
          if (!isTherapistInBooking) {
            return false;
          }
          
          // Check time overlap
          const bStart = b.startTime || b.start_time;
          const bEnd = b.endTime || b.end_time;
          const overlap = timesOverlap(bookingStartTime, bookingEndTime, bStart, bEnd);
          
          if (overlap) {
            console.log(`   ⚠️ ${t.name} has conflicting booking: ${b.treatment} at ${bStart}-${bEnd}`);
          }
          
          return overlap;
        });
        
        if (conflictingBookings.length > 0) {
          console.log(`❌ Filtered out ${t.name}: Not available (${conflictingBookings.length} conflicting booking(s))`);
          return false;
        }
        
        console.log(`✅ Qualified: ${t.name} - Has skill, rostered, correct gender, not assigned, AVAILABLE`);
        return true;
      });

      console.log(`   Found ${qualified.length} qualified and available therapists`);
      setQualifiedTherapists(qualified);
    } catch (error) {
      console.error('Error fetching qualified therapists:', error);
      setError('Failed to load qualified therapists');
    } finally {
      setLoadingQualifiedTherapists(false);
    }
  };

  // Handle replacing a therapist
  const handleReplaceTherapist = async (newTherapistName) => {
    try {
      setIsLoading(true);
      setError(null);

      // Update the therapists array with the new therapist
      const updatedTherapists = [...(booking.therapists || [])];
      updatedTherapists[therapistToReplace.index] = {
        ...updatedTherapists[therapistToReplace.index],
        name: newTherapistName
      };

      // Make API call to update booking with new therapist assignment
      const bookingDateForDelete = originalBookingDate || booking.date;
      
      // Delete old booking
      const deleteResponse = await apiDelete(`/api/bookings/${bookingDateForDelete}/${booking.id || booking.bookingId}`);

      if (!deleteResponse.ok) {
        throw new Error('Failed to delete old booking');
      }

      // Create new booking with updated therapists
      const treatment = config.treatments.find(t => t.name === editFormData.treatment);
      const confirmPayload = {
        guestName: editFormData.guestName,
        guestId: booking.guestId || booking.guest_id,
        treatment: editFormData.treatment,
        startTime: booking.startTime || booking.start_time,
        endTime: booking.endTime || booking.end_time,
        room: booking.room,
        therapist: updatedTherapists.find(t => t.is_primary)?.name || updatedTherapists[0]?.name,
        therapists: updatedTherapists,
        date: editFormData.date,
        generalInstruction: editFormData.generalInstruction,
        treatmentInstruction: editFormData.treatmentInstruction,
        status: editFormData.status || booking.status,
        duration: editFormData.duration,
        customDuration: editFormData.customDuration
      };

      applyPackageEntitlementToConfirm(confirmPayload, upgradePreview);
      applyPackageScheduleBillingToConfirm(confirmPayload);

      const confirmResponse = await apiPost('/api/schedule/confirm', confirmPayload);

      const confirmResult = await confirmResponse.json();
      
      if (!confirmResponse.ok || confirmResult.error) {
        throw new Error(confirmResult.error || 'Failed to confirm booking with new therapist');
      }

      // Success!
      alert(`✅ Therapist replaced successfully!\n${therapistToReplace.name} → ${newTherapistName}`);
      setShowTherapistReplaceModal(false);
      setTherapistToReplace(null);
      
      // Refresh the booking data
      onSchedulingComplete({ success: true });
      
    } catch (error) {
      console.error('Error replacing therapist:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!show || !booking) return null;

  const treatmentDetails = getTreatmentDetails(booking.treatment || booking.service);

  return (
    <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 ${show ? '' : 'hidden'}`}>
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#E4C984' }}>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <h2 className="text-2xl font-semibold text-gray-800">{booking.guestName || booking.guest_name}</h2>
            </div>
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md">
              {error}
              <button 
                onClick={() => setError(null)} 
                className="float-right text-red-500 hover:text-red-700"
              >
                <span className="text-xl">&times;</span>
              </button>
            </div>
          )}

          {/* Upgrade Approved Badge */}
          {upgradeApproval && upgradePreview && (
            <div className="mb-4 p-4 border-2 border-green-500 rounded-lg bg-green-50">
              <div className="space-y-2">
                {/* First row: Status and Amount */}
                <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <span className="text-green-700 font-semibold text-lg">✓ Upgrade Approved</span>
                    <span className="text-green-600 text-sm">
                      ({upgradeApproval.upgrade_status === 'complimentary' && 'Complimentary'}
                      {upgradeApproval.upgrade_status === 'discounted' && 'Discounted'}
                      {upgradeApproval.upgrade_status === 'payable' && 'Payable'})
                    </span>
                    {/* Cancel Upgrade Button */}
                    <button
                      onClick={async () => {
                        const originalTreatmentName =
                          booking.original_treatment ||
                          upgradePreview?.original_treatment_name ||
                          'the original treatment';
                        const confirmed = window.confirm(
                          `Cancel this upgrade?\n\n` +
                          `This will:\n` +
                          `• Restore the appointment to ${originalTreatmentName}\n` +
                          `• Remove the upgrade billing charge (₹${(() => {
                            if (upgradeApproval.upgrade_status === 'complimentary') return '0';
                            const upgradeAmt = parseFloat(upgradePreview.upgrade_amount);
                            const discount = parseFloat(upgradeApproval.upgrade_discount_amount) || 0;
                            const netUpgrade = upgradeAmt - discount;
                            const taxRate = parseFloat(upgradePreview.tax_rate);
                            const tax = netUpgrade * (taxRate / 100);
                            return (netUpgrade + tax).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                          })()})\n` +
                          `• Keep the same date and time\n\n` +
                          `Continue?`
                        );
                        
                        if (!confirmed) return;
                        
                        const reason = window.prompt('Reason for cancelling upgrade:');
                        if (!reason) return;
                        
                        try {
                          const response = await apiPost(`/api/guest-cleanup/cancel-upgrade/${booking.id || booking.bookingId}`, { reason });
                          
                          if (!response.ok) {
                            const errorData = await response.json();
                            throw new Error(errorData.detail || 'Failed to cancel upgrade');
                          }
                          
                          const result = await response.json();
                          alert(`✅ ${result.message}`);
                          
                          // Close modal and refresh
                          onClose();
                          if (onSchedulingComplete) onSchedulingComplete();
                        } catch (error) {
                          console.error('Error cancelling upgrade:', error);
                          alert(`❌ Error cancelling upgrade: ${error.message}`);
                        }
                      }}
                      className="text-xs px-2 py-1 border border-red-400 text-red-600 rounded hover:bg-red-50 transition-colors"
                    >
                      ✕ Cancel Upgrade
                    </button>
                </div>
                  <div className="text-right">
                    <div className="text-xs text-green-600">Guest Pays</div>
                    <div className="text-xl font-bold text-green-700">
                      ₹{(() => {
                        if (upgradeApproval.upgrade_status === 'complimentary') return '0.00';
                        const upgradeAmt = parseFloat(upgradePreview.upgrade_amount);
                        const discount = parseFloat(upgradeApproval.upgrade_discount_amount) || 0;
                        const netUpgrade = upgradeAmt - discount;
                        const taxRate = parseFloat(upgradePreview.tax_rate);
                        const tax = netUpgrade * (taxRate / 100);
                        return (netUpgrade + tax).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                      })()}
              </div>
              </div>
            </div>

                {/* Second row: Breakdown */}
                <div className="text-sm text-green-600 flex items-center gap-4">
                  <span>
                    Base: ₹{parseFloat(upgradePreview.upgrade_amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                  {upgradeApproval.upgrade_discount_amount > 0 && (
                    <span>
                      - Discount: ₹{parseFloat(upgradeApproval.upgrade_discount_amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  )}
                  {upgradeApproval.upgrade_status !== 'complimentary' && (
                    <span>
                      + Tax ({upgradePreview.tax_rate}%)
                    </span>
                  )}
                  {/* Placeholder for approver - will be filled when user authentication is implemented */}
                  <span className="ml-auto text-xs italic">
                    Approved by: {upgradeApproval.approved_by || 'Current User'}
                  </span>
              </div>

                {/* Third row: Instructions */}
                <div className="text-sm text-green-700 font-medium pt-2 border-t border-green-300">
                  → Click "Check Availability" to preview schedule, then "Save Changes" to apply
              </div>
              </div>
            </div>
          )}

          {upgradePreview?.is_upgrade && !upgradeApproval && (
            <div className="mb-4 p-4 border-2 border-amber-400 rounded-lg bg-amber-50">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-amber-800 font-semibold">Upgrade charge</div>
                  <div className="text-sm text-amber-800 mt-1">
                    {upgradePreview.original_treatment_name || 'Current treatment'} → {upgradePreview.new_treatment_name || editFormData.treatment}
                  </div>
                  <div className="text-sm text-amber-700 mt-1">
                    Base ₹{parseFloat(upgradePreview.upgrade_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    {' + Tax '}
                    {upgradePreview.tax_rate}% ₹{parseFloat(upgradePreview.tax_on_upgrade || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-amber-700">Guest pays</div>
                  <div className="text-xl font-bold text-amber-900">
                    ₹{parseFloat(upgradePreview.total_additional_cost || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>
              </div>
              <div className="text-sm text-amber-800 mt-2">Save Changes to review and approve this charge.</div>
            </div>
          )}

          {treatmentChangeNotice && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 text-blue-800 rounded-md text-sm">
              {treatmentChangeNotice}
            </div>
          )}

          {/* Main Editable Section */}
          <div className="mb-6 p-4 border-2 border-[#E4C984] rounded-lg bg-[#FFFBF0]">
            <div className="grid grid-cols-2 gap-4">
              {/* Treatment */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Treatment *</label>
                <TreatmentSelector
                  treatments={Array.isArray(config.treatments) ? config.treatments : []}
                  selectedTreatment={editFormData.treatment}
                  onSelect={handleTreatmentChange}
                  disabled={isLoading}
                />
                {upgradePreview?.is_upgrade && !upgradeApproval && (
                  <div className="mt-2 p-3 border border-amber-400 rounded-md bg-amber-50 text-sm text-amber-900">
                    <div className="font-semibold">Upgrade charge</div>
                    <div className="mt-1">
                      {upgradePreview.original_treatment_name || 'Current treatment'} → {upgradePreview.new_treatment_name || editFormData.treatment}
                    </div>
                    <div className="mt-1">
                      Guest pays ₹{parseFloat(upgradePreview.total_additional_cost || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      {' '}(base ₹{parseFloat(upgradePreview.upgrade_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      {' + tax '}{upgradePreview.tax_rate}%)
                    </div>
                  </div>
                )}
                {upgradePreview && !upgradePreview.is_upgrade && editFormData.treatment && editFormData.treatment !== (booking.treatment || booking.service) && (
                  <div className="mt-2 p-3 border border-green-300 rounded-md bg-green-50 text-sm text-green-900">
                    <div className="font-semibold">No additional charge</div>
                    <div className="mt-1">Guest pays ₹0. {upgradePreview.message || 'This stays within the package.'}</div>
                  </div>
                )}
              </div>

              {/* Therapist(s) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Therapist{(editFormData.therapists?.length > 1 || booking.therapistsCount > 1) ? 's' : ''} *
                  {(editFormData.therapists?.length > 1 || booking.therapistsCount > 1) && (
                    <span className="ml-2 text-xs text-blue-600 font-semibold">
                      {(editFormData.therapists?.length || booking.therapistsCount)} THERAPISTS
                    </span>
                  )}
                </label>
                {/* Always use the same UI pattern - read-only with edit icon */}
                {(editFormData.therapists?.length > 1 || booking.therapistsCount > 1) ? (
                  <>
                    <div className="w-full p-2.5 text-sm border border-gray-300 rounded-md bg-gray-50 text-gray-700">
                      {(editFormData.therapists?.length ? editFormData.therapists : (booking.therapists || [])).map((t, idx) => (
                        <div key={idx} className="py-1 flex items-center justify-between group hover:bg-gray-100 px-2 rounded">
                          <span>
                            {idx + 1}. {t.name} {t.is_primary ? '(PRIMARY)' : ''}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleEditTherapist(t, idx)}
                            disabled={isLoading}
                            className="ml-2 text-blue-600 hover:text-blue-800 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-30"
                            title={`Replace ${t.name}`}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      💡 Hover and click ✏️ to replace individual therapists, or "Check Availability" to reassign all.
                    </p>
                  </>
                ) : (
                  <>
                    {/* Single therapist - same pattern as multi */}
                    <div className="w-full p-2.5 text-sm border border-gray-300 rounded-md bg-gray-50 text-gray-700">
                      <div className="py-1 flex items-center justify-between group hover:bg-gray-100 px-2 rounded">
                        <span>
                          {editFormData.therapist || 'Not assigned'}
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            const therapists = editFormData.therapists?.length
                              ? editFormData.therapists
                              : [{
                                name: editFormData.therapist || booking.therapist,
                                is_primary: true,
                                level: 3,
                                gender: ''
                              }];
                            handleEditTherapist(therapists[0], 0);
                          }}
                          disabled={isLoading}
                          className="ml-2 text-blue-600 hover:text-blue-800 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-30"
                          title={`Replace ${editFormData.therapist || booking.therapist}`}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      💡 Hover and click ✏️ to replace therapist with an available alternative.
                    </p>
                  </>
                )}
              </div>

              {/* Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                <input
                  type="date"
                  value={editFormData.date}
                  onChange={handleInputChange}
                  name="date"
                  className="w-full p-2.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E4C984] focus:border-transparent"
                />
              </div>

              {/* Time */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">Time *</label>
                  <button
                    type="button"
                    onClick={handleOpenSlotPicker}
                    disabled={isLoading || !editFormData.date || !editFormData.treatment}
                    className="text-xs font-semibold text-[#92400e] hover:text-[#78350f] underline disabled:opacity-40 disabled:no-underline"
                  >
                    Browse available slots
                  </button>
                </div>
                <input
                  type="time"
                  value={editFormData.preferredTime}
                  onChange={handleInputChange}
                  name="preferredTime"
                  className="w-full p-2.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E4C984] focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Pick from available slots above, or enter a custom time and use Check Availability.
                </p>
              </div>

              {/* Duration */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Duration (minutes) *
                  {editFormData.customDuration && (
                    <span className="ml-2 text-xs text-blue-600 font-semibold">CUSTOM</span>
                  )}
                </label>
                <input
                  type="number"
                  value={editFormData.duration || treatmentDetails.duration || 50}
                  onChange={(e) => {
                    const newDuration = parseInt(e.target.value) || 0;
                    const treatment = config.treatments?.find(t => t.name === editFormData.treatment);
                    const defaultDuration = treatment?.duration || 50;
                    
                    setEditFormData(prev => ({
                      ...prev,
                      duration: newDuration,
                      customDuration: newDuration !== defaultDuration
                    }));
                    clearSchedulePreviewForFieldChange();
                  }}
                  name="duration"
                  min="15"
                  max="240"
                  step="5"
                  className="w-full p-2.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E4C984] focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Default: {treatmentDetails.duration} min • Range: 15-240 min
                </p>
              </div>
            </div>
          </div>

          {/* Current Booking Info */}
          <div className="grid grid-cols-2 gap-x-8 gap-y-2 mb-6 p-4 bg-gray-50 rounded-md text-sm">
            <div><span className="font-semibold text-gray-600">ROOM:</span> <span className="text-gray-800">{booking.room || '-'}</span></div>
            <div><span className="font-semibold text-gray-600">DURATION:</span> <span className="text-gray-800">{treatmentDetails.duration} minutes</span></div>
            <div><span className="font-semibold text-gray-600">TIME:</span>{' '}
              <span className="text-gray-800">
                {schedulePreview?.startTime != null &&
                schedulePreview?.endTime != null &&
                previewMatchesSelectedTime(schedulePreview, editFormData.preferredTime)
                  ? `${formatTimeDisplay(schedulePreview.startTime)} - ${formatTimeDisplay(schedulePreview.endTime)} (confirmed)`
                  : editFormData.preferredTime
                    ? `${formatTimeDisplay(editFormData.preferredTime)} selected — browse slots or Check Availability to confirm`
                    : `${formatTimeDisplay(booking.startTime || booking.start_time)} - ${formatTimeDisplay(booking.endTime || booking.end_time)}`}
              </span>
            </div>
            <div><span className="font-semibold text-gray-600">STATUS:</span> <span className="text-gray-800 capitalize">{booking.status || 'Confirmed'}</span></div>
            {guestRoom && (
              <div><span className="font-semibold text-gray-600">GUEST ROOM:</span> <span className="text-gray-800">{guestRoom}</span></div>
            )}
          </div>

          {/* Instructions - Side by Side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">General Instructions</label>
              <textarea
                name="generalInstruction"
                value={editFormData.generalInstruction}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E4C984] focus:border-transparent text-sm"
                rows="3"
                placeholder="Enter any general instructions or notes"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Treatment Instructions</label>
              <textarea
                name="treatmentInstruction"
                value={editFormData.treatmentInstruction}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E4C984] focus:border-transparent text-sm"
                rows="3"
                placeholder="Enter specific instructions for this treatment"
              />
            </div>
          </div>

          {/* Additional Preferences - Collapsible */}
          <div className="mb-6">
            <button
              onClick={() => setShowAdditionalPreferences(!showAdditionalPreferences)}
              className="flex items-center justify-between w-full p-3 text-left border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              <span className="text-sm font-medium text-gray-700">Show Additional Preferences</span>
              {showAdditionalPreferences ? (
                <ChevronUp className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-500" />
              )}
            </button>

            {showAdditionalPreferences && (
              <div className="mt-3 p-4 border border-gray-200 rounded-md bg-gray-50 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  {/* Room Preference */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Room Preference</label>
                    <select
                      value={editFormData.room}
                      onChange={handleInputChange}
                      name="room"
                      className="w-full p-2 text-sm border border-gray-300 rounded-md"
                    >
                      <option value="">Any Room</option>
                      {Object.entries(config.rooms || {}).map(([roomName, roomData]) => (
                        <option key={roomName} value={roomName}>
                          {roomName} {roomData.location ? `(${roomData.location})` : ''}
                        </option>
                      ))}
                    </select>
              </div>

              {/* Preferred Time of Day */}
              <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Time of Day</label>
                <select
                  value={editFormData.preferredTimeOfDay}
                  onChange={handleInputChange}
                  name="preferredTimeOfDay"
                      className="w-full p-2 text-sm border border-gray-300 rounded-md"
                >
                  <option value="">Any Time</option>
                  <option value="morning">Morning (9 AM - 1 PM)</option>
                  <option value="afternoon">Afternoon (1 PM - 5 PM)</option>
                </select>
              </div>

              {/* Gender Preference */}
              <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Gender Preference</label>
                <select
                  value={editFormData.genderPreference}
                  onChange={handleInputChange}
                  name="genderPreference"
                      className="w-full p-2 text-sm border border-gray-300 rounded-md"
                >
                  <option value="">Any Gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
                <p className="mt-1 text-xs text-amber-600">
                  ⚠️ For gender-sensitive treatments, therapist gender will automatically match guest gender for safety, regardless of this preference.
                </p>
              </div>

                  {/* Status */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      name="status"
                      value={editFormData.status}
                      onChange={handleInputChange}
                      className="w-full p-2 text-sm border border-gray-300 rounded-md"
                    >
                      <option value="confirmed">Confirmed</option>
                      <option value="checked_in">Checked In</option>
                      <option value="waitlist">Wait List</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
            </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-between gap-3">
              <button
              onClick={() => onDelete(booking)}
                disabled={isLoading}
              className="px-4 py-2.5 text-sm font-medium text-white bg-gray-700 rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50"
            >
              Delete
            </button>
            
            <div className="flex gap-3">
              <button
                onClick={handleSaveChanges}
                disabled={isLoading}
                className="px-5 py-2.5 text-sm font-medium rounded-md transition-colors"
                style={{ backgroundColor: '#E4C984', color: '#171717' }}
              >
                {isLoading ? 'Saving...' : 'Save Changes'}
              </button>
              
              <button
                onClick={handleCheckAvailability}
                disabled={isLoading || !editFormData.guestName || !editFormData.treatment}
                className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors disabled:opacity-50"
              >
                    {isLoading ? 'Checking...' : 'Check Availability'}
                  </button>
              
              <button
                onClick={onClose}
                disabled={isLoading}
                className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancel
                  </button>
            </div>
          </div>
        </div>
      </div>

      <ScheduleSlotPickerModal
        open={showSlotPicker}
        onClose={() => setShowSlotPicker(false)}
        onSelect={handleSlotPickerSelect}
        treatmentName={editFormData.treatment}
        date={editFormData.date}
        loading={slotPickerLoading}
        slots={slotPickerSlots}
        failureHints={slotPickerHints}
      />

      {/* Upgrade Approval Modal (old flow, kept for compatibility) */}
      <UpgradeApprovalModal
        open={showUpgradeModal}
        onClose={handleUpgradeCancel}
        upgradePreview={upgradePreview}
        onApprove={handleUpgradeApprove}
      />

      {/* Combined Preview Modal (new flow - shows schedule + upgrade together) */}
      <CombinedPreviewModal
        open={showCombinedModal}
        onClose={handleCombinedModalCancel}
        schedulePreview={schedulePreview}
        upgradePreview={upgradePreview}
        originalBooking={booking}
        onConfirm={handleCombinedModalConfirm}
        isLoading={isLoading}
        confirmError={combinedModalError}
        showSuccess={showCombinedSuccess}
        successMessage={combinedSuccessMessage}
      />

      {/* Therapist Replacement Modal */}
      {showTherapistReplaceModal && therapistToReplace && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-lg max-w-md w-full shadow-xl">
            <div className="p-6">
              {/* Header */}
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">Replace Therapist</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Replacing: <span className="font-medium">{therapistToReplace.name}</span>
                    {therapistToReplace.isPrimary && <span className="ml-2 text-xs text-blue-600">(PRIMARY)</span>}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowTherapistReplaceModal(false);
                    setTherapistToReplace(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Loading State */}
              {loadingQualifiedTherapists && (
                <div className="py-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E4C984] mx-auto"></div>
                  <p className="text-sm text-gray-600 mt-2">Loading qualified therapists...</p>
                </div>
              )}

              {/* Error State */}
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">
                  {error}
                </div>
              )}

              {/* Therapist Selection */}
              {!loadingQualifiedTherapists && qualifiedTherapists.length > 0 && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Replacement Therapist
                  </label>
                  <div className="max-h-60 overflow-y-auto border border-gray-300 rounded-md">
                    {qualifiedTherapists.map((therapist, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => handleReplaceTherapist(therapist.name)}
                        disabled={isLoading}
                        className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-200 last:border-b-0 transition-colors disabled:opacity-50"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium text-gray-800">{therapist.name}</div>
                            <div className="text-xs text-gray-500 mt-0.5">
                              {therapist.gender && `${therapist.gender} • `}
                              {therapist.level && `Level ${therapist.level}`}
                            </div>
                          </div>
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    ℹ️ Only showing therapists who: have the required skills, are rostered for this date, match gender requirements (if needed), are not already assigned, and are available for this time slot.
                  </p>
                </div>
              )}

              {/* No Therapists Available */}
              {!loadingQualifiedTherapists && qualifiedTherapists.length === 0 && !error && (
                <div className="py-8 text-center">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                  <p className="text-sm text-gray-600 mt-2">No qualified therapists available</p>
                  <p className="text-xs text-gray-500 mt-1">
                    All matching therapists are already assigned or unavailable.
                  </p>
                </div>
              )}

              {/* Footer */}
              <div className="flex justify-end mt-4 pt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowTherapistReplaceModal(false);
                    setTherapistToReplace(null);
                  }}
                  disabled={isLoading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditBookingModal;

